/**
 * Live room status over WebSocket.
 *
 * A dropped socket reconnects with backoff; while it is down the interface
 * keeps showing the last known state and says so, rather than blanking.
 */
import { useEffect, useRef, useState } from "react";

import { getAccessToken } from "@/lib/api";
import type { RoomDisplay } from "@/lib/types";

export interface RoomStateMessage {
  room_id: string;
  room_name: string;
  display: RoomDisplay;
  display_settings: Record<string, unknown>;
}

type Handler = (message: RoomStateMessage) => void;

export function useRoomStream(enabled: boolean, onRoomState: Handler) {
  const [connected, setConnected] = useState(false);
  const handlerRef = useRef(onRoomState);
  handlerRef.current = onRoomState;

  useEffect(() => {
    if (!enabled) return;
    let socket: WebSocket | null = null;
    let attempt = 0;
    let closed = false;
    let timer: number | undefined;

    const connect = () => {
      const token = getAccessToken();
      if (!token) return;
      const protocol = window.location.protocol === "https:" ? "wss" : "ws";
      socket = new WebSocket(
        `${protocol}://${window.location.host}/api/ws/portal?token=${encodeURIComponent(token)}`,
      );
      socket.onopen = () => {
        attempt = 0;
        setConnected(true);
      };
      socket.onmessage = (event) => {
        try {
          const frame = JSON.parse(event.data);
          if (frame.type === "room.state") handlerRef.current(frame.payload);
        } catch {
          /* a malformed frame is ignored rather than breaking the page */
        }
      };
      socket.onclose = () => {
        setConnected(false);
        if (closed) return;
        attempt += 1;
        timer = window.setTimeout(connect, Math.min(1000 * 2 ** attempt, 30000));
      };
      socket.onerror = () => socket?.close();
    };

    connect();
    return () => {
      closed = true;
      window.clearTimeout(timer);
      socket?.close();
    };
  }, [enabled]);

  return connected;
}

/** Is the browser online? Used for the portal's offline banner. */
export function useOnline(): boolean {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);
  return online;
}
